import axios, { type AxiosRequestConfig, type AxiosError, type AxiosResponse } from "axios";

import { t } from "@/locales/i18n";
import userStore from "@/store/userStore";

import { toast } from "sonner";
import type { Result } from "#/api";

// 创建 axios 实例
const axiosInstance = axios.create({
	baseURL: import.meta.env.VITE_APP_BASE_API,
	timeout: 50000,
	headers: { "Content-Type": "application/json;charset=utf-8" },
});

// 防抖函数
export const debounce = <T extends (...args: any[]) => any>(fn: T, delay: number) => {
	let timer: any | null = null;
	return (...args: Parameters<T>): Promise<ReturnType<T>> => {
		if (timer) clearTimeout(timer);
		return new Promise((resolve) => {
			timer = setTimeout(() => {
				resolve(fn(...args));
			}, delay);
		});
	};
};

// 请求拦截
axiosInstance.interceptors.request.use(
	(config) => {
		console.log(config.url);
		if (config.url?.includes("login")) {
			return config;
		}

		const state = userStore.getState();
		const accessToken = state.userToken.accessToken;

		config.headers.Authorization = `Bearer ${accessToken || ""}`;

		// if (!hasValidServer()) {
		// useServerStore.getState().actions.logout();
		// throw new Error(t("sys.connect.noServer"));
		// }
		return config;
	},
	(error) => {
		return Promise.reject(error);
	},
);

// 响应拦截
axiosInstance.interceptors.response.use(
	(res: AxiosResponse<Result>) => {
		if (!res.data) throw new Error(t("sys.api.apiRequestFailed"));

		const { code, data, msg } = res.data;
		// 业务请求成功
		if (code === 200) {
			return res.data;
		}

		// 429跳过
		if (res.status === 429) {
			return data;
		}

		// 404不提示
		if (res.status === 404) {
			return data;
		}

		// 业务请求错误
		throw new Error(msg || t("sys.api.apiRequestFailed"));
	},
	(error: AxiosError<Result>) => {
		const { response, message } = error || {};

		const errMsg = response?.data?.msg || message || t("sys.api.errorMessage");
		toast.error(errMsg, {
			position: "top-center",
		});

		const status = response?.status;
		if (status === 401) {
			userStore.getState().actions.clearUserInfoAndToken();
		}
		return Promise.reject(error);
	},
);

class APIClient {
	get<T = any>(config: AxiosRequestConfig): Promise<T> {
		return this.request({ ...config, method: "GET" });
	}

	post<T = any>(config: AxiosRequestConfig): Promise<T> {
		return this.request({ ...config, method: "POST" });
	}

	put<T = any>(config: AxiosRequestConfig): Promise<T> {
		return this.request({ ...config, method: "PUT" });
	}

	delete<T = any>(config: AxiosRequestConfig): Promise<T> {
		return this.request({ ...config, method: "DELETE" });
	}

	request<T = any>(config: AxiosRequestConfig): Promise<T> {
		return new Promise((resolve, reject) => {
			axiosInstance
				.request<any, AxiosResponse<Result>>(config)
				.then((res: AxiosResponse<Result>) => {
					resolve(res as unknown as Promise<T>);
				})
				.catch((e: Error | AxiosError) => {
					reject(e);
				});
		});
	}
}
export default new APIClient();
